import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * Supports optional per-slide countdown timers (slides hide and are skipped
 * once their deadline passes) and pauses rotation while the expandable menu
 * panel is open.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The interval ID for the countdown timers.
   * @type {number|undefined}
   */
  #countdownInterval = undefined;

  /**
   * The expandable menu panel, when present.
   * @type {HTMLDetailsElement|null}
   */
  #panelDetails = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#initCountdowns();
    this.#initPanel();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.suspend();
    clearInterval(this.#countdownInterval);
    this.#countdownInterval = undefined;
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#panelDetails?.removeEventListener('toggle', this.#handlePanelToggle);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(index) {
    const slides = this.refs.slides ?? [];
    const length = slides.length;
    if (length === 0) return;

    let relativeIndex = index % length;
    if (relativeIndex < 0) {
      relativeIndex += length;
    }

    // Skip slides whose countdown has expired so rotation never stalls on them.
    let guard = 0;
    while (slides[relativeIndex]?.dataset.expired === 'true' && guard < length) {
      relativeIndex = (relativeIndex + 1) % length;
      guard += 1;
    }

    this.#current = relativeIndex;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /**
   * Finds countdown elements and ticks them every second.
   */
  #initCountdowns() {
    const countdowns = this.querySelectorAll('[data-countdown]');
    if (countdowns.length === 0) return;

    this.#tickCountdowns(countdowns);

    // Lock each timer to its initial (widest) width. Days only ever count down,
    // so the first render is the widest the timer will ever be. This guarantees
    // the timer never reflows — and never shifts the SHOP NOW toggle — even on a
    // year-long campaign where the day count drops from 3 digits to 1.
    countdowns.forEach((element) => {
      const timeElement = element.querySelector('[data-countdown-time]');
      if (!timeElement || !timeElement.textContent) return;
      const width = timeElement.getBoundingClientRect().width;
      if (width > 0) timeElement.style.minInlineSize = `${Math.ceil(width)}px`;
    });

    this.#countdownInterval = setInterval(() => this.#tickCountdowns(countdowns), 1000);
  }

  /**
   * Updates every countdown and hides slides whose deadline has passed.
   * @param {NodeListOf<Element>} countdowns
   */
  #tickCountdowns(countdowns) {
    countdowns.forEach((element) => {
      const endSeconds = parseInt(element.getAttribute('data-countdown-end') ?? '', 10);
      const timeElement = element.querySelector('[data-countdown-time]');

      // Ignore unparseable/implausible deadlines (e.g. a date the merchant typed
      // that Liquid could not parse). Keep the slide visible without a timer
      // instead of hiding the whole message.
      if (!Number.isFinite(endSeconds) || endSeconds < 1000000000) {
        if (timeElement) timeElement.textContent = '';
        return;
      }

      const remaining = endSeconds * 1000 - Date.now();
      if (remaining <= 0) {
        if (timeElement) timeElement.textContent = this.#format(0);
        this.#expireSlide(element);
        return;
      }

      if (timeElement) timeElement.textContent = this.#format(remaining);
    });
  }

  /**
   * Formats a millisecond duration as `0D:00H:00M:00S`.
   * @param {number} ms
   * @returns {string}
   */
  #format(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => `${value}`.padStart(2, '0');

    return `${days}D:${pad(hours)}H:${pad(minutes)}M:${pad(seconds)}S`;
  }

  /**
   * Hides the slide that owns an expired countdown and advances if needed.
   * @param {Element} countdownElement
   */
  #expireSlide(countdownElement) {
    const slide = countdownElement.closest('slideshow-slide');
    if (!slide || slide.dataset.expired === 'true') return;

    const wasVisible = slide.getAttribute('aria-hidden') === 'false';
    slide.dataset.expired = 'true';
    slide.hidden = true;
    slide.setAttribute('aria-hidden', 'true');

    if (wasVisible) this.next();
  }

  /**
   * Pauses rotation while the expandable menu panel is open.
   */
  #initPanel() {
    this.#panelDetails = this.closest('.announcement-bar')?.querySelector('.announcement-bar__panel-details') ?? null;
    this.#panelDetails?.addEventListener('toggle', this.#handlePanelToggle);
  }

  #handlePanelToggle = () => {
    if (this.#panelDetails?.open) {
      this.suspend();
    } else {
      this.resume();
    }
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
